import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Form, Row, Col, Button } from "react-bootstrap";

function ResourceAllocationForm({
  role,
  usersCreatedBy,
  resourceAllocations,
  editData,
  setEditData,
  onSave,
  users,
}) {
  const currentUser = usersCreatedBy?.[0];
  const createdUsers = currentUser?.createdUsers || [];

  // Validation schema
  const validationSchema = Yup.object().shape({
    ...(role === "superadmin" && {
      assistantSuperAdminId: Yup.string().required("Assistant Superadmin is required"),
    }),
    managerId: Yup.string()
      .required("Manager is required")
      .test(
        "unique-manager-assignment",
        "This manager is already assigned to this Assistant Superadmin.",
        function (managerId) {
          const { assistantSuperAdminId } = this.parent;
          if (!managerId || !assistantSuperAdminId) return true;

          return !resourceAllocations.some(
            (ra) =>
              ra.managerId === managerId &&
              ra.assistantSuperAdminId === assistantSuperAdminId &&
              (!editData || editData._id !== ra._id)
          );
        }
      ),
    staffId: Yup.string()
      .required("Staff is required")
      .test(
        "unique-staff-assignment",
        "This staff is already assigned to this Manager.",
        function (staffId) {
          const { managerId } = this.parent;
          if (!staffId || !managerId) return true;

          return !resourceAllocations.some(
            (ra) =>
              ra.staffId === staffId &&
              ra.managerId === managerId &&
              (!editData || editData._id !== ra._id)
          );
        }
      ),
  });

  const formik = useFormik({
    initialValues: {
      assistantSuperAdminId: role === "assistant superadmin" ? usersCreatedBy?.[0]?._id : "",
      managerId: "",
      staffId: "",
      role,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      onSave(values, resetForm);
    },
  });

  const [managersOptions, setManagersOptions] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);

  const getMergedOptions = (assistantId) => {
    const alloc = resourceAllocations?.find(
      (ra) => ra.assistantSuperAdminId === assistantId
    );

    const allocManagers = alloc?.resource?.map((r) => r.manager) || [];
    const allocStaffs = alloc?.resource?.flatMap((r) => r.staffs || []) || [];

    const createdManagers = createdUsers?.filter((u) => u.role === "manager") || [];
    const createdStaffs = createdUsers?.filter((u) => u.role === "staff") || [];

    let mergedManagers =
      role === "superadmin"
        ? users[0]?.children?.filter((i) => i.role === "manager") || []
        : [...new Map([...allocManagers, ...createdManagers].map((u) => [u._id, u])).values()];

    let mergedStaffs =
      role === "superadmin"
        ? users[0]?.children?.filter((i) => i.role === "staff") || []
        : [...new Map([...allocStaffs, ...createdStaffs].map((u) => [u._id, u])).values()];

    return { mergedManagers, mergedStaffs };
  };

  useEffect(() => {
    if (formik.values.assistantSuperAdminId) {
      const { mergedManagers, mergedStaffs } = getMergedOptions(
        formik.values.assistantSuperAdminId
      );
      setManagersOptions(mergedManagers);
      setStaffOptions(mergedStaffs);
    } else {
      setManagersOptions([]);
      setStaffOptions([]);
    }
  }, [formik.values.assistantSuperAdminId, resourceAllocations, users, role]);

  return (
    <Form noValidate onSubmit={formik.handleSubmit}>
      <Row>
        {role === "superadmin" && (
          <Col md={4}>
            <Form.Group className="mb-2">
              <Form.Label>Assistant Superadmin</Form.Label>
              <Form.Select
                name="assistantSuperAdminId"
                value={formik.values.assistantSuperAdminId}
                onChange={formik.handleChange}
                isInvalid={
                  formik.touched.assistantSuperAdminId &&
                  !!formik.errors.assistantSuperAdminId
                }
              >
                <option value="">Select</option>
                {createdUsers
                  .filter((u) => u.role === "assistant superadmin")
                  .map((user) => (
                    <option key={user._id} value={user._id}>
                      {user.name}
                    </option>
                  ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {formik.errors.assistantSuperAdminId}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        )}

        <Col md={4}>
          <Form.Group className="mb-2">
            <Form.Label>Manager</Form.Label>
            <Form.Select
              name="managerId"
              value={formik.values.managerId}
              onChange={formik.handleChange}
              isInvalid={formik.touched.managerId && !!formik.errors.managerId}
            >
              <option value="">Select</option>
              {managersOptions?.map((user) => (
                <option key={user._id} value={user._id}>
                  {user.name}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {formik.errors.managerId}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group className="mb-2">
            <Form.Label>Staff</Form.Label>
            <Form.Select
              name="staffId"
              value={formik.values.staffId}
              onChange={formik.handleChange}
              isInvalid={formik.touched.staffId && !!formik.errors.staffId}
            >
              <option value="">Select</option>
              {staffOptions?.map((user) => (
                <option key={user._id} value={user._id}>
                  {user.name}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {formik.errors.staffId}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col className="text-end mt-3">
          <Button
            variant="secondary"
            onClick={() => {
              setEditData(null);
              formik.resetForm();
            }}
            className="me-2"
          >
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            {editData ? "Update" : "Allocate"}
          </Button>
        </Col>
      </Row>
    </Form>
  );
}

export default ResourceAllocationForm;